<template>
  <main class="wrapper-content w-100">
    <!-- Loading -->
    <LmComponentLoading v-if="isLoading" fixed />

    <h1>Gestión de Características</h1>
    <p>
      En esta sección se crean o editan características de Lumbre unificando todo su contexto para realizar una gestión global de los registros que conforman la funcionalidad.
    </p>
    <div class="mt-4">
      <b-card no-body v-if="features">
        <b-tabs pills card vertical>
          <b-tab
            v-for="(feature, index) of features"
            :key="feature._id"
            :title="feature.name"
            lazy
          >
            <b-card-text>
              <h4 class="mb-4">{{ feature.name }} en el servicio {{ feature.serviceName }}</h4>

              <b-row>
                <LmTextInput
                  class="col-12 col-md-3"
                  label="ID del formbuilder"
                  v-model="feature.formbuilder"
                />
                <LmTextInput
                  class="col-12 col-md-3"
                  label="ID del listbuilder"
                  v-model="feature.listbuilder"
                />
                <LmTextInput
                  class="col-12 col-md-3"
                  label="ID del frontendmenu"
                  v-model="feature.frontendmenu"
                  help="Si el es un punto de menú anidado, especificarlo como id-menu/id-hijo"
                />
                <LmTextInput
                  class="col-12 col-md-3"
                  label="ID del frontendroute"
                  v-model="feature.frontendroute"
                />
                <LmCheckbox
                  class="col-12"
                  title="Aplicar en"
                  :options="applyOptions"
                  type="multiple"
                  v-model="feature.applyOn"
                />
                <LmTreeSelect
                  class="col-12"
                  label="Privilegios que podrán operar"
                  multiple
                  :options="privilegesOptions"
                  :value="privilegeValues[index].privileges"
                  @input="onChangePrivileges($event, index)"
                />
                <LmButton
                  class="col-auto"
                  value="Eliminar"
                  action="delete"
                  buttonClass="btn-danger"
                  @click="onDelete(feature, index)"
                />
                <LmButton
                  class="col-auto"
                  value="Guardar"
                  @click="onSave(feature)"
                />
              </b-row>

            </b-card-text>
          </b-tab>

          <b-tab
            title="Agregar característica"
            lazy
            active
          >
            <b-card-text>
              <b-row>
                <LmTextInput
                  class="col-6"
                  label="Nombre de la característica"
                  v-model="newFeature.name"
                  :v="$v.newFeature.name"
                />
                <LmTextInput
                  class="col-6"
                  label="Nombre del servicio"
                  v-model="newFeature.serviceName"
                  :v="$v.newFeature.serviceName"
                />
                <LmTextInput
                  class="col-12 col-md-3"
                  label="ID del formbuilder"
                  v-model="newFeature.formbuilder"
                />
                <LmTextInput
                  class="col-12 col-md-3"
                  label="ID del listbuilder"
                  v-model="newFeature.listbuilder"
                />
                <LmTextInput
                  class="col-12 col-md-3"
                  label="ID del frontendmenu"
                  v-model="newFeature.frontendmenu"
                  help="Si el es un punto de menú anidado, especificarlo como id-menu/id-hijo"
                />
                <LmTextInput
                  class="col-12 col-md-3"
                  label="ID del frontendroute"
                  v-model="newFeature.frontendroute"
                />
                <LmCheckbox
                  class="col-12"
                  title="Aplicar en"
                  :options="applyOptions"
                  type="multiple"
                  v-model="newFeature.applyOn"
                />
                <LmTreeSelect
                  class="col-6"
                  label="Privilegios que podrán operar"
                  multiple
                  :options="privilegesOptions"
                  v-model="newFeature.privileges"
                />
              </b-row>
              <LmButton
                value="Guardar"
                @click="onCreateNewFeature()"
              />
            </b-card-text>
          </b-tab>

        </b-tabs>
      </b-card>
    </div>
  </main>
</template>

<script>
// Components
import LmTreeSelect from '@/components/form/elements/LmTreeSelect'
import LmButton from '@/components/form/elements/LmButton'
import LmTextInput from '@/components/form/elements/LmTextInput'
import LmCheckbox from '@/components/form/elements/LmCheckbox'

// Utils
import lmToasts from '@/mixins/lmToasts.js'
import commonsApi from '@/api/commons'
import _ from 'lodash'
import { required } from 'vuelidate/lib/validators'

export default {
  mixins: [lmToasts],
  data () {
    return {
      isLoading: false,
      features: null,
      newFeature: {
        name: '',
        serviceName: '',
        formbuilder: '',
        listbuilder: '',
        frontendmenu: '',
        frontendroute: '',
        privileges: [],
        applyOn: ['create', 'update', 'delete']
      },
      privilegesOptions: null
    }
  },
  validations: {
    newFeature: {
      name: {
        required
      },
      serviceName: {
        required
      }
    }
  },
  computed: {
    privilegeValues () {
      const values = []
      const features = _.cloneDeep(this.features)
      features.forEach(feature => {
        if (feature?.privileges) {
          feature.privileges = feature.privileges.map(priv => {
            return this.privilegesOptions.find(option => option.codigo_privilegio === priv)
          })
        }
        values.push(feature)
      })
      return values
    },
    applyOptions () {
      return [
        {
          text: 'Creación',
          value: 'create'
        },
        {
          text: 'Actualización',
          value: 'update'
        },
        {
          text: 'Borrado',
          value: 'delete'
        }
      ]
    }
  },
  components: {
    LmTreeSelect,
    LmButton,
    LmTextInput,
    LmCheckbox
  },
  methods: {
    async onSave (feature) {
      try {
        this.isLoading = true
        const res = await commonsApi.axiosPutWrapper(`/features/${feature._id}`, feature)
        this.lmToastSuccessMessage(`Se guardó ${res?.data?.name || ' la característica'} correctamente.`, 'Guardado con éxito')
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onDelete (feature, index) {
      try {
        this.isLoading = true
        const res = await commonsApi.axiosDeleteWrapper(`/features/${feature._id}`)
        this.features.splice(index, 1)
        this.lmToastSuccessMessage(`Se eliminó ${res?.data?.name || ' la característica'} correctamente.`, 'Eliminado con éxito')
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    async onCreateNewFeature () {
      try {
        this.isLoading = true
        this.validateForm(this.$v)
        this.newFeature.privileges = this.newFeature.privileges.map(priv => priv.codigo_privilegio)
        const responseNewFeature = await commonsApi.axiosPostWrapper(`/features`, this.newFeature)
        this.features.push(responseNewFeature.data)
        this.newFeature = {
          name: '',
          serviceName: '',
          formbuilder: '',
          listbuilder: '',
          frontendmenu: '',
          frontendroute: '',
          privileges: [],
          applyOn: ['create', 'update', 'delete']
        }
        this.$v.$reset()
      } catch (error) {
        console.log(error)
      } finally {
        this.isLoading = false
      }
    },
    onChangePrivileges (event, index) {
      this.features[index].privileges = event.map(e => e.codigo_privilegio)
      console.log(this.features)
    },
    validateForm (validation) {
      validation.$touch()
      if (validation.$error) {
        this.lmToastDangerMessage(this.$t('form_with_errors'), 'Error')
        throw new Error('validation error')
      } else {
        if (validation.$pending || validation.$error) {
          throw new Error('validation error')
        }
      }
    }
  },
  async created () {
    try {
      this.isLoading = true
      const responsePrivileges = await commonsApi.axiosGetWrapper('/codificatorbuilder/codificators/60ef50e476b81943ec4d99da')
      this.privilegesOptions = responsePrivileges.data.items

      const responseFeatures = await commonsApi.axiosGetWrapper('/features/all')
      this.features = responseFeatures.data.items
    } catch (error) {
      console.log(error)
    } finally {
      this.isLoading = false
    }
  }
}
</script>
